import { useFormik } from "formik";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { confirmPasswordReset } from "../../behavior/api/auth";
import { PageState } from "../../constants";
import { forgotPasswordFormSchema as validationSchema } from "../../utils/validationSchemas";
import { Button, Text } from "../elements";
import type { LargeAlertProps } from "../elements/LargeAlert";
import { Password } from "../forms";

interface ForgotPasswordFormProps {
    setShowAlert: Dispatch<SetStateAction<LargeAlertProps>>;
}

const ForgotPasswordForm = ({ setShowAlert }: ForgotPasswordFormProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);

    const initialValues = {
        newPassword: "",
        confirmNewPassword: "",
    };

    const { values, errors, touched, handleSubmit, handleChange, handleBlur } = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            const oobCode = searchParams.get("oobCode");
            const email = searchParams.get("email");
            if (oobCode && email) {
                setLoading(true);
                try {
                    const response = await confirmPasswordReset(oobCode, values.newPassword);

                    if ("type" in response) {
                        if (response.type === "WEAK_PASSWORD" || response.message === "weak-password") {
                            toast.error(t("ForgotPassword.ResetYourPassword.Error_WeakPassword"));
                            return;
                        }
                        if (response.type === "INVALID_CODE" || response.message === "invalid-code") {
                            setShowAlert((prevState) => ({
                                ...prevState,
                                title: t("ForgotPassword.ResetYourPassword.Error_InvalidCode_Title"),
                                subtitle: t("ForgotPassword.ResetYourPassword.Error_InvalidCode_Subtitle"),
                                type: "warning",
                            }));
                            return;
                        }
                    }

                    const redirectUrl = searchParams.get("redirectUrl");
                    if ("payload" in response && response.payload) {
                        if (redirectUrl) {
                            window.location.replace(decodeURIComponent(redirectUrl));
                            return;
                        }
                        const encodedEmail = encodeURIComponent(email);
                        navigate(`/?state=${PageState.PASSWORD_RESET}&email=${encodedEmail}`, { replace: true });
                    } else {
                        toast.error(t("ForgotPassword.ResetYourPassword.Error_ResetPasswordFailed"));
                    }
                } catch (e) {
                    toast.error(
                        e instanceof Error
                            ? e.message
                            : t("ForgotPassword.ResetYourPassword.Error_ResetPasswordFailed"),
                    );
                } finally {
                    setLoading(false);
                }
            } else {
                toast.error(t("ForgotPassword.ResetYourPassword.Error_MalformedResetUrl"));
            }
        },
    });

    return (
        <form className="forgot-password-form-wrapper" onSubmit={handleSubmit}>
            <Password
                label={t("ForgotPassword.ResetYourPassword.Label_NewPassword")}
                name="newPassword"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.newPassword}
                error={errors.newPassword}
                touched={touched.newPassword}
            />
            <Password
                label={t("ForgotPassword.ResetYourPassword.Label_ConfirmNewPassword")}
                name="confirmNewPassword"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.confirmNewPassword}
                error={errors.confirmNewPassword}
                touched={touched.confirmNewPassword}
            />
            <Button fullWidth submit className="mt-2" disabled={loading}>
                {t("ForgotPassword.ResetYourPassword.ButtonText_SetNewPassword")}
            </Button>
            <div className="sign-in-instead-button-wrapper">
                <Text>{t("ForgotPassword.ResetYourPassword.ButtonText_RememberYourPassword")}</Text>
                <Button link secondary className="custom-btn" to="/">
                    {t("ForgotPassword.ResetYourPassword.ButtonText_SignInInstead")}
                </Button>
            </div>
        </form>
    );
};

export default ForgotPasswordForm;
